import { useContext, useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthContext from "../../context/AuthContext";
import { authApi, userApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { API_BASE_URL } from "../../constants";
import PageHeader from "../PageHeader";
import CopyField from "./CopyField";
import {
  envFileContents,
  hostedServerName,
  mcpAddHostedCommand,
} from "./agentSetup";

/**
 * Your own API token — what the MCP server sends when it acts as YOU
 * (same reach as your login). Agents get their own tokens on the
 * Overview tab; this page is only for your personal one.
 */
const MyToken = () => {
  const [token, setToken] = useState(null);
  const { isLoggedIn, user } = useContext(AuthContext);
  const [username, setUsername] = useState(user?.username || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rotateArmed, setRotateArmed] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [rotated, setRotated] = useState(false);

  useEffect(() => {
    if (!rotateArmed) return undefined;
    const t = setTimeout(() => setRotateArmed(false), 5000);
    return () => clearTimeout(t);
  }, [rotateArmed]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!isLoggedIn) {
      setError("You must be logged in to view token information.");
      return;
    }
    setSubmitting(true);
    setError(null);
    setRotated(false);
    try {
      const response = await authApi.getAuthToken(username, password);
      setToken(response.data?.token ?? null);
      if (!response.data?.token) setError("The server did not return a token.");
    } catch (err) {
      setToken(null);
      setError(
        err.response?.status === 400
          ? "Invalid username or password."
          : apiErrorMessage(err, "Failed to get token. Please try again."),
      );
    } finally {
      setSubmitting(false);
      // Don't keep the password around after it's been used.
      setPassword("");
    }
  }

  // Rotate re-confirms the password: it's a full-reach credential. The
  // password field is cleared after reveal, so the user types it again.
  async function handleRotate() {
    if (!rotateArmed) {
      setRotateArmed(true);
      return;
    }
    setRotating(true);
    setError(null);
    try {
      const { data } = await userApi.rotateToken(password);
      setToken(data.token);
      setRotated(true);
    } catch (err) {
      setError(
        err.response?.status === 400
          ? "Wrong password — type it again below, then rotate."
          : apiErrorMessage(err, "Failed to rotate the token."),
      );
    } finally {
      setRotating(false);
      setRotateArmed(false);
      setPassword("");
    }
  }

  return (
    <div className="page-container mt-6 pt-4">
      <PageHeader
        title="My API token"
        subtitle="The token the MCP server uses when it acts as you — it has the same reach as your login. Agents get their own tokens on Overview."
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <Card>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Confirm your password to reveal the token. It&apos;s the same
                token every time until you rotate it. Changing your password
                does <strong>not</strong> invalidate it — if it leaked, rotate
                it below.
              </p>
              <form onSubmit={handleSubmit}>
                <div className="mb-4 grid gap-1.5">
                  <Label htmlFor="formUsername">Username</Label>
                  <Input
                    id="formUsername"
                    type="text"
                    placeholder="Enter username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>
                <div className="mb-4 grid gap-1.5">
                  <Label htmlFor="formPassword">Password</Label>
                  <Input
                    id="formPassword"
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="mb-4" disabled={submitting}>
                  {submitting ? "Getting token…" : "Show my token"}
                </Button>
              </form>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {token && (
                <div className="mt-3">
                  {rotated && (
                    <Alert className="py-2">
                      <AlertDescription className="text-sm">
                        Rotated. The old token stopped working; update{" "}
                        <code>.env.local</code> and any hosted-MCP entry (
                        <code>claude mcp remove mono-hosted -s user</code>, then
                        add it again) anywhere it was used.
                      </AlertDescription>
                    </Alert>
                  )}
                  <CopyField id="my-token" label="Token" value={token} />
                  <CopyField
                    id="my-env"
                    label="Save as mcp_server/.env.local (the default identity)"
                    value={envFileContents(token, API_BASE_URL)}
                    multiline
                  />
                  <CopyField
                    id="my-mcp-hosted"
                    label="Or, from any machine — the hosted MCP (no clone, no env file)"
                    value={mcpAddHostedCommand(
                      hostedServerName(),
                      token,
                      API_BASE_URL,
                    )}
                    multiline
                  />
                  <p className="text-sm text-muted-foreground">
                    That hosted line contains your token: pasting it writes the
                    token to your shell history and to{" "}
                    <code>~/.claude.json</code>. Lead with a space so your shell
                    skips history, or type the header by hand.
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="password"
                      placeholder="Password again to rotate"
                      aria-label="Password to rotate"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="max-w-[260px]"
                    />
                    <Button
                      type="button"
                      variant={rotateArmed ? "secondary" : "outline"}
                      disabled={rotating || !password}
                      onClick={handleRotate}
                    >
                      {rotating
                        ? "Rotating…"
                        : rotateArmed
                          ? "Confirm rotate"
                          : "Rotate my token"}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MyToken;
